import { useParams } from "react-router-dom";
import { PathParams, RoutingPath } from "../../../../routes/routing-path";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MetaPrintShopOrderGetResponse } from "../../../../api/sift/front/api-print-meta-shop-order";
import { GetJsonListData, printActions, PrintShopOrderState } from "../../../../slices/sift/print-slice";
import { useApiActions } from "../../../../hooks/use-api-actions";
import { ApiImagesCopyPost, ApiImagesGet } from '../../../../api/front/images/api-images';
import { ApiPrintImagesGet, PrintImagesDataEntity, PrintImagesGetResponse } from "../../../../api/sift/front/api-print-image";
import { TrimUiManager } from "../../../../trim-editor/manager/ui/trim.ui.manager";
import { TrimEditableImage } from "../../../../trim-editor/manager/image-edit/trim.editable-image";
import { TrimEditableImageManager } from "../../../../trim-editor/manager/editable-image/trim.editable-image.manager";
import { IMAGES_KIND } from "../../../../layout-editor/manager/image-processor/image-processor.manager";
import { RecordData } from "../../../../utilities/utility.type";
import { ceil, cloneDeep } from "lodash";
import { hasRecordValue } from "../../../../utilities/has-record-value";
import { PrintDeliveryDataEntity, PrintItemDataEntity } from "../../../../models/sift/json/print-model";
import { TrimThumbManager } from "../../../../trim-editor/manager/thumb/trim.thumb.manager";
import { PrintUploadProgressList } from "../../../../trim-editor/manager/image-processor/trim.image-processor.manager";
import { dialogActions } from "../../../dialog/slice/dialog-slice";
import { push } from "connected-react-router";
import { SameOrderError } from "../../order-preparation/order-preparation";
import { useLoading } from "../../../ui/loading/hooks/use-loading";
import { LaboOrderCompChecker } from "../../../../utilities/labo-order-comp-checker";
import { apiActions } from '../../../../slices/api-slice';

type ImageKindType = '1' | '2' | '3' | '4' | '5' | '6';
type PrintPreparationThumbData = {
  imageId: string,
  thumbBase64?: string,
}
export type PrintPreparationThumbDataEntity = {

}
type Props = {};
export const usePrintOrder = (props?: Props) => {
  const {} = props ?? {};
  const { kijshopCd, shopOrderId, orderId } = useParams<PathParams>();
  const dispatch = useAppDispatch();
  const { asyncApiRun } = useApiActions();
  const loadingActions = useLoading({});
  const {
    currentData,
    currentMeta,
  } = useAppSelector((state) => ({
    currentData: state.print.printShopOrderData[shopOrderId],
    currentMeta: state.print.printShopOrderMeta[shopOrderId],
  }));
  const isLaboOrdered = useRef<boolean>(false);
  const [itemMemo, setItemMemo] = useState<Partial<RecordData<string>>>({});
  const [itemThumbData, setItemThumbData] = useState<Partial<RecordData<PrintPreparationThumbData[]>>>({});
  const [progress, setProgress] = useState<null | { queue: number, done: number, error: number }>(null);
  const [isLabOrderRetry, setIsLabOrderRetry] = useState(false);
  const progressView = useMemo(() => {
    if (!progress) return '';
    const {
      queue,
      done,
      error,
    } = progress;
    if (!queue && !done && !error) return '';
    return `${(((done + error) / (queue + done + error)) * 100).toFixed(2)}`;
  }, [progress]);


  const filterPattern = useCallback((editableImage: TrimEditableImage) => {
    return (shopOrderId === editableImage.shopOrderId)
      && editableImage.kind === IMAGES_KIND.PRINT;
  }, [
    shopOrderId,
  ]);
  const [_editableImages, setEditableImages] = useState<TrimEditableImage[]>(TrimEditableImageManager.ins.list.filter(filterPattern));
  const editableImages = useMemo(() => {
    return _editableImages.filter((v) => {
      return v.flags.uploaded;
    })
  }, [_editableImages]);

  const handlerChangeMemo = useCallback((id: string, value: string) => {
    itemMemo[id] = value;
    setItemMemo({ ...itemMemo });
  }, [itemMemo]);

  const handlerBlurMemo = useCallback(() => {
    if (!currentData) {
      return;
    }
    const data = cloneDeep(currentData.meta);
    data.getItemList().forEach((item) => {
      const memo = itemMemo?.[item.id] ?? '';
      if (memo.length <= 50) {
        item.memo = memo;
      }
    });
    if (hasRecordValue(itemMemo)) {
      if (!data.meta) {
        data.meta = {};
      }
      data.meta.checkItemMemo = true;
    }
    dispatch(printActions.shopOrder({ kijshopCd, shopOrderId }).update(
      data,
    ));
  }, [
    dispatch,
    currentData,
    kijshopCd, shopOrderId,
    itemMemo,
  ]);

  const resetItemMemo = useCallback(() => {
    setItemMemo({});
  }, []);

  // メタデータ取得
  const getMetaData = useCallback(() => {
    return new Promise<MetaPrintShopOrderGetResponse>((resolve, reject) => {
      dispatch(printActions.getOrderMeta(
        kijshopCd, shopOrderId,
        {
          onSuccess: (res) => {
            resolve(res);
          },
          onError: () => {
            reject();
          }
        }
      ));
    })
  }, [dispatch, kijshopCd, shopOrderId]);

  // JSONデータ取得
  const getData = useCallback(() =>  new Promise<PrintShopOrderState & { original: GetJsonListData }>((resolve, reject) => {
    dispatch(printActions.getShopOrderData(
      kijshopCd, shopOrderId,
      {
        onSuccess: (res, original) => {
          const items = res.meta.getItemList();
          const memo: RecordData<string> = {};
          items.forEach((v) => {
            memo[v.id] = v.memo ?? '';
          });
          console.log('[OrderDataJSON]:', res.meta);
          console.group('[ItemDataJSON]');
          res.image.forEach((v) => {
            console.group(`id: ${v.itemId}_${v.id}`);
            console.log(v);
            console.groupEnd();
          })
          console.groupEnd();
          setItemMemo(memo);
          resolve({...res, original});
        },
        onError: () => {
          reject();
        }
      }
    ))
  }), [dispatch, kijshopCd, shopOrderId]);

  const createEditableImage = useCallback((
    request: PrintImagesDataEntity,
    callback?: (editableImage: TrimEditableImage) => void,
  ) => {
    TrimUiManager.ins.emit('r->t:trim-add-image:restore', {
      kijshopCd,
      shopOrderId,
      onSuccess: (ei) => callback?.(ei),
      onError: () => {},
      data: request,
    })
  }, [
    kijshopCd,
    shopOrderId,
  ]);

    const createThumbImage = useCallback(async(editableImage: TrimEditableImage, itemData: PrintItemDataEntity) => {
      return new Promise<string | undefined>((resolve) => {
        const itemId = itemData.id;
        const target = itemData.images.find((v) => v.id === editableImage.id);
        if (!target || !itemId) {
          throw new Error('存在しません。');
        };
        const mng = new TrimThumbManager();
        const directFlg = !Number(target.rotate) ? '1' : '2';
        mng.makeThumbOutCanvas({
          kijshopCd,
          shopOrderId,
          orderId: itemId,
          item: itemData,
          editableImgId: editableImage.id,
          directFlg,
        }).then((res) => {
          if (res) {
            setItemThumbData((prev) => {
              const current = prev[itemData.id];
              const pushData = { imageId: editableImage.id, thumbBase64: res };
              if (current) {
                current.push(pushData);
              } else {
                prev[itemData.id] = [pushData];
              }
              return {...prev};
            })
            const setData = {
              imageId: editableImage.id,
              itemId: itemData.id,
              base64: res,
            }
          }
          resolve(res);
        });
      })

    }, [
      kijshopCd, shopOrderId,
    ]);


  // 画像一覧の取得
  const getImages = useCallback((
    param?: {
      callback?: {
        onCreateEditableImage?: (editableImage: TrimEditableImage) => void,
        onError?: (e: any) => void;
      }, options?: {
        apiName?: string;
        ignoreSystemError?: boolean;
        ignoreTimeout?: boolean;
    },
    ignoreCreateEditableImage?: boolean,
  },
  ) => {
    return new Promise<PrintImagesGetResponse>((resolve, reject) => {
      asyncApiRun(
        new ApiPrintImagesGet({ kijshopCd, shopOrderId }),
        param?.callback,
        {
          ...param?.options,
          ignoreSystemError: param?.options?.ignoreSystemError ?? true,
        }
      ).then((res) => {
        resolve(res);
        if (!param?.ignoreCreateEditableImage) {
          res.fileList.forEach((v) => {
              createEditableImage(v, param?.callback?.onCreateEditableImage);
            });
          }
      }).catch((e) => reject(e));
    });
  }, [
    kijshopCd, shopOrderId,
    asyncApiRun,
    createEditableImage,
  ]);
  const getThumbImages = useCallback((
    param?: {
      callback?: {
        onCreateEditableImage?: (editableImage: TrimEditableImage) => void,
        onError?: (e: any) => void;
      }, options?: {
        apiName?: string;
        ignoreSystemError?: boolean;
        ignoreTimeout?: boolean;
    },
      ignoreCreateEditableImage?: boolean,
      isPreview?: boolean,
  },
  ) => {
    return new Promise<PrintImagesGetResponse>((resolve, reject) => {
      asyncApiRun(
        new ApiPrintImagesGet({ kijshopCd, shopOrderId, kind: param?.isPreview ? IMAGES_KIND.THUMB_PREVIEW : IMAGES_KIND.TMP_THUMB }),
        param?.callback,
        {
          ...param?.options,
          ignoreSystemError: param?.options?.ignoreSystemError ?? true,
        }
      ).then((res) => {
        resolve(res);
      }).catch((e) => reject(e));
    });
  }, [
    kijshopCd, shopOrderId,
    asyncApiRun,
  ]);

  const imageUpload = useCallback((uploadFiles: File[], callback?: (editableImage: TrimEditableImage) => void) => {
    TrimUiManager.ins.emit('r->t:trim-add-image', {
      files: uploadFiles,
      kijshopCd,
      shopOrderId,
      onUploaded: (p) => callback?.(p.editableImage),
    });
  }, [
    kijshopCd,
    shopOrderId,
  ]);

  const labOrder = useCallback(async() => {
    // ラボ発注していれば
    if (isLaboOrdered.current) {
      return;
    }
    // 商品がない　または商品のうち一つでもラボ発注可じゃないものがあれば
    if (
      !currentData?.meta.getItemList().length
      || currentData?.meta.getItemList().some((v) => v.status !== 10 )
    ) {
      return;
    }
    // 確認
    await new Promise<void>((resolve) => {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: [
          '発注処理を行います。よろしいですか？',
        ],
        buttons: [
          { label: 'いいえ', callback: () => dispatch(dialogActions.popAll()) },
          { label: 'はい', callback: () => {
            dispatch(dialogActions.popAll());
            resolve();
          } },
        ]
      }));
    });
    if (
      currentData.meta.deliveryData?.type === 'customer'
    ) {
      const deliveryData = currentData.meta.deliveryData;
      const customerDeliveryRequire: (string | undefined)[] = [
        deliveryData.customerName,
        deliveryData.address1,
        deliveryData.prefecture,
        deliveryData.zipCode,
        deliveryData.phoneNumber,
        deliveryData.timeZone,
        deliveryData.formName,
        deliveryData.pdfPath,
      ];
      if (customerDeliveryRequire.some((v) => !v)) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: [
          '配送先情報が登録されていません。',
          '配送先情報の登録後、再度発注を行なってください。',
        ],
        buttons: [{
          label: 'OK',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        }],
      }));
      return;
      }
    }
    loadingActions.setLoadingMessage('ラボ発注中です...');
    dispatch(printActions.shopOrder({
      kijshopCd, shopOrderId,
    }).labOrder({
      isRetry: isLabOrderRetry,
      callback: {
        success: () => {
          loadingActions.setLoadingMessage('');
          dispatch(dialogActions.pushMessage({
            title: '確認',
            message: [
              '発注処理を行いました',
              '',
              '受注完了メールに納品日が記載されていますので',
              '合わせてご確認ください',
            ],
            buttons: [
              {
                label: 'OK',
                callback: () => {
                  dispatch(dialogActions.pop());
                  dispatch(push(RoutingPath.cnv.print({ kijshopCd })));
                },
              },
            ],
          }));
        },
        error: () => {
          loadingActions.setLoadingMessage('');
          isLaboOrdered.current = false;
        },
        customError: ({ code, message }) => {
          loadingActions.setLoadingMessage('');
          if (code === 'center') {
            dispatch(dialogActions.pop());
            /* 注文番号重複 */
            dispatch(dialogActions.push({
              title: '注文番号重複エラー',
              element: <SameOrderError />,
            }));
          } else {
            if (message.length) {
              isLaboOrdered.current = false;
              dispatch(dialogActions.pushMessage({
                title: 'エラー',
                message,
                buttons: [
                  { label: 'OK', callback: () => dispatch(dialogActions.popAll()) }
                ]
              }))
            }
          }
        }
      }
    }));
  }, [
    shopOrderId, kijshopCd,
    dispatch,
    currentData,
    currentMeta,
    loadingActions,
    isLabOrderRetry,
  ]);

  const onLoadMeta = useCallback((v?: MetaPrintShopOrderGetResponse) => {
    if (
      v?.status === 'ラボ発注中'
      && LaboOrderCompChecker.checkTimeoutForPrint({ orderCompleteDate: v.orderCompleteDate ?? '' })
    ) {
      setIsLabOrderRetry(true);
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: [
          '発注処理が正常に完了しませんでした',
          '「再発注」ボタンをクリックしてください',
        ],
        buttons: [{
          label: 'OK',
          callback: () => {
            dispatch(dialogActions.pop());
          }
        }]
      }));
    }
  }, [
    dispatch,
  ]);

  useEffect(() => {
    const set = (param: { list: TrimEditableImage[] }) => {
      setEditableImages(param.list.filter(filterPattern));
    };
    const setProgressData = (param: Map<string, PrintUploadProgressList>) => {
      const target = param.get(shopOrderId);
      const {
        queue = [],
        complete = [],
        error = [],
      } = target ?? {};
      if (progress && !target) {
        setProgress({ queue: 0, done: progress.done + progress.queue, error: progress.error });
        window.setTimeout(() => {
          setProgress({ queue: queue.length, done: complete.length, error: error.length });
        }, 3000)
      } else {
        setProgress({ queue: queue.length, done: complete.length, error: error.length });
      }
    }

    TrimUiManager.ins.on('t->r:change-editable-image-list', set);
    TrimUiManager.ins.on('r->t:get-current-queue-count', setProgressData);
    return (() => {
      TrimUiManager.ins.off('t->r:change-editable-image-list', set);
      TrimUiManager.ins.off('r->t:get-current-queue-count', setProgressData);
    });
  }, [
    shopOrderId,
    kijshopCd,
  ]);

  return {
    getMetaData,
    getData,
    getImages,
    editableImages,
    itemMemo,
    handlerChangeMemo,
    setItemThumbData,
    createThumbImage,
    uploadProgress: progress,
    uploadProgressView: progressView,
    imageUpload,
    getThumbImages,
    memo: {
      value: itemMemo,
      onChange: handlerChangeMemo,
      blur: handlerBlurMemo,
      resetItemMemo,
    },
    loadingActions,
    labOrder,
    onLoadMeta,
    isLabOrderRetry,
  }
}
